import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash, PencilSimple, CheckCircle, Warning, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, can } from "@/context/AuthContext";

export default function Incidencias() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ titulo: "", descripcion: "", estado: "abierta" });
  const isAdmin = can(user, "admin");

  const load = useCallback(async () => {
    const params = {};
    if (filter) params.estado = filter;
    const { data } = await api.get("/incidencias", { params });
    setItems(data);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/incidencias/${editing.id}`, form);
      else await api.post("/incidencias", form);
      toast.success(editing ? "Incidencia actualizada" : "Incidencia creada");
      setShowForm(false); setEditing(null);
      setForm({ titulo: "", descripcion: "", estado: "abierta" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleEstado = async (it) => {
    try {
      await api.put(`/incidencias/${it.id}`, { estado: it.estado === "abierta" ? "resuelta" : "abierta" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar incidencia?")) return;
    try { await api.delete(`/incidencias/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#2563EB] font-bold">Gestión</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tighter">Incidencias</h1>
        </div>
        <button onClick={() => { setEditing(null); setForm({ titulo: "", descripcion: "", estado: "abierta" }); setShowForm(true); }}
          className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-5 h-11 flex items-center gap-2 font-display font-bold uppercase tracking-wide text-sm shadow-md shadow-blue-500/20"
          style={{ borderRadius: 8 }}>
          <Plus size={18} weight="bold" /> Nueva incidencia
        </button>
      </div>

      <div className="flex gap-2">
        {["", "abierta", "resuelta"].map(v => (
          <button key={v} onClick={() => setFilter(v)}
            className={`h-10 px-4 text-sm font-bold uppercase tracking-wider transition ${filter===v ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"}`}
            style={{borderRadius:8}}>
            {v === "" ? "Todas" : v}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 shadow-card" style={{ borderRadius: 12 }}>
        {items.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">Sin incidencias registradas</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map(it => (
              <div key={it.id} className="p-4 flex items-start gap-3">
                <button onClick={() => toggleEstado(it)}
                  className={`h-8 w-8 shrink-0 flex items-center justify-center rounded-full ${it.estado === "resuelta" ? "bg-[#22C55E]/10 text-[#16a34a]" : "bg-[#F97316]/10 text-[#ea580c]"}`}
                  title={it.estado === "resuelta" ? "Marcar como abierta" : "Marcar como resuelta"}>
                  {it.estado === "resuelta" ? <CheckCircle size={20} weight="fill" /> : <Warning size={20} weight="fill" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-slate-900">{it.titulo}</span>
                    <span className={`text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 border ${it.estado === "resuelta" ? "text-[#16a34a] border-[#22C55E]/30 bg-[#22C55E]/10" : "text-[#ea580c] border-[#F97316]/30 bg-[#F97316]/10"}`} style={{borderRadius:4}}>
                      {it.estado}
                    </span>
                  </div>
                  {it.descripcion && <div className="text-sm text-slate-600 mt-1 whitespace-pre-line">{it.descripcion}</div>}
                  <div className="text-[10px] text-slate-500 mt-1 uppercase tracking-widest">
                    {new Date(it.created_at).toLocaleString("es-ES")} • {it.created_by_name}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => { setEditing(it); setForm({ titulo: it.titulo, descripcion: it.descripcion || "", estado: it.estado }); setShowForm(true); }}
                    className="h-8 w-8 border border-slate-200 hover:border-[#2563EB] hover:text-[#2563EB] flex items-center justify-center" style={{borderRadius:8}}>
                    <PencilSimple size={14} />
                  </button>
                  {isAdmin && (
                    <button onClick={() => remove(it.id)} className="h-8 w-8 border border-slate-200 hover:border-[#EF4444] hover:text-[#EF4444] flex items-center justify-center" style={{borderRadius:8}}>
                      <Trash size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={submit} className="bg-white border border-slate-200 w-full max-w-lg p-6 space-y-4 shadow-xl" style={{ borderRadius: 12 }}>
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-2xl uppercase">{editing ? "Editar incidencia" : "Nueva incidencia"}</h2>
              <button type="button" onClick={() => setShowForm(false)} className="text-slate-500 hover:text-slate-900"><X size={20} /></button>
            </div>
            <label className="block"><span className="block text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mb-1.5">Título *</span>
              <input required value={form.titulo} onChange={e => setForm({...form, titulo: e.target.value})}
                className="w-full bg-slate-50 border border-slate-200 px-3 h-10 text-slate-900 focus:border-[#2563EB] outline-none" />
            </label>
            <label className="block"><span className="block text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mb-1.5">Descripción</span>
              <textarea rows={4} value={form.descripcion} onChange={e => setForm({...form, descripcion: e.target.value})}
                className="w-full bg-slate-50 border border-slate-200 px-3 py-2 text-slate-900 focus:border-[#2563EB] outline-none" />
            </label>
            <label className="block"><span className="block text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mb-1.5">Estado</span>
              <select value={form.estado} onChange={e => setForm({...form, estado: e.target.value})}
                className="w-full bg-slate-50 border border-slate-200 px-3 h-10 text-slate-900 focus:border-[#2563EB] outline-none">
                <option value="abierta">Abierta</option><option value="resuelta">Resuelta</option>
              </select>
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-5 h-11 border border-slate-200" style={{borderRadius:8}}>Cancelar</button>
              <button type="submit" className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-5 h-11 font-display font-bold uppercase tracking-wide" style={{borderRadius:8}}>{editing ? "Guardar" : "Crear"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
